import { Route, Switch, useHistory, useParams } from "react-router-dom";
import AttributionInsights from "./AttributionInsights";
import CreativesInsights from "./CreativesInsights";
import KeywordInsights from "./KeywordInsights";
import { ApiDimension } from "@incendium/api";
import withPagetitle from "HoC/withPagetitle";
import { Box, IconButton, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import { useCallback, useMemo, useState } from "react";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useSelectedProject } from "Hooks";
import { Settings } from "@mui/icons-material";
import { SelectedAttributesProvider } from "features/analytics";
import AnalayticsFilterDrawer from "features/analytics/components/AnalayticsFilterDrawer";

const AttrInsightsHoc = withPagetitle(AttributionInsights);
const CreativesInsightsHoc = withPagetitle(CreativesInsights);
const KeywordInsightsHoc = withPagetitle(KeywordInsights);

type InsightDimension = ApiDimension.DIMENSION_SOURCE | ApiDimension.DIMENSION_CAMPAIGN | ApiDimension.DIMENSION_CONTENT;

const mapping: Record<string, InsightDimension> = {
  platforms: ApiDimension.DIMENSION_SOURCE,
  campaigns: ApiDimension.DIMENSION_CAMPAIGN,
  "ad-sets": ApiDimension.DIMENSION_CONTENT,
};

function MarketingPerformancePaidMediaPage({ }) {
  const history = useHistory();
  const { pathByName } = useProtectedRoutes();
  const { selectedProject } = useSelectedProject();
  const { insight } = useParams<{ insight: string }>();
  const [open, setOpen] = useState(false);

  const tab = useMemo(() => {
    return mapping[insight as InsightDimension];
  }, [insight]);

  const onTabChange = useCallback((value) => {
    if (!value || value === tab) return;
    const insightKey = Object.keys(mapping).find(key => mapping[key] === value);
    history.push(pathByName(RouteNames.PAID_MEDIA, { insight: insightKey, clientId: selectedProject?.clientId, projectId: selectedProject?.id }));
  }, [pathByName, selectedProject?.clientId, selectedProject?.id, history, tab]);
  return (
    <SelectedAttributesProvider initialAttributes={[]}>
      <GlassCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <ToggleButtonGroup
            size="small"
            value={tab}
            exclusive
            color="secondary"
            onChange={(_, value) => {
              onTabChange(value);
            }}
          >
            <ToggleButton value={ApiDimension.DIMENSION_SOURCE}>Platforms</ToggleButton>
            <ToggleButton value={ApiDimension.DIMENSION_CAMPAIGN}>Campaigns</ToggleButton>
            <ToggleButton value={ApiDimension.DIMENSION_CONTENT}>Content / Ad-Sets</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => setOpen(true)}>
            <Settings />
          </IconButton>
        </Stack>
      </GlassCard>
      <Box mt={2} pb={4}>
        <Switch>
          <Route path="/clients/:clientId/projects/:projectId/analyse/standard-reports/paid-media/platforms">
            <AttrInsightsHoc
              chartKeyScope="platforms"
              marketingDimension={ApiDimension.DIMENSION_SOURCE}
              dimensionTitle="Platform"
              title="Paid Media - Platforms"
            />
          </Route>
          <Route path="/clients/:clientId/projects/:projectId/analyse/standard-reports/paid-media/campaigns">
            <AttrInsightsHoc
              chartKeyScope="campaigns"
              marketingDimension={ApiDimension.DIMENSION_CAMPAIGN}
              dimensionTitle="Campaign"
              title="Paid Media - Campaigns"
            />
          </Route>
          <Route path="/clients/:clientId/projects/:projectId/analyse/standard-reports/paid-media/ad-sets">
            <AttrInsightsHoc
              chartKeyScope="ad_sets"
              marketingDimension={ApiDimension.DIMENSION_CONTENT}
              dimensionTitle="Content / Ad-Sets"
              title="Paid Media - Content / Ad-Sets"
            />
          </Route>
          <Route path="/clients/:clientId/projects/:projectId/analyse/standard-reports/paid-media/creatives">
            <CreativesInsightsHoc title="Paid Media - Creatives" />
          </Route>
          <Route path="/clients/:clientId/projects/:projectId/analyse/standard-reports/paid-media/keywords">
            <KeywordInsightsHoc title="Paid Media - Keywords" />
          </Route>
        </Switch>
      </Box>
      <AnalayticsFilterDrawer open={open} onClose={() => setOpen(false)} />
    </SelectedAttributesProvider>
  );
}

export default MarketingPerformancePaidMediaPage;
